import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { docsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, FileText, ArrowLeft, Trash2, Share2, Clock, Search } from 'lucide-react';

export default function DocsHub() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState({ owned: [], shared: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showShare, setShowShare] = useState(null);
  const [shareEmail, setShareEmail] = useState('');

  useEffect(() => {
    docsApi.list().then(d => { setDocs(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    try {
      const doc = await docsApi.create({ title: 'Untitled Document' });
      navigate(`/docs/${doc.doc_id}`);
    } catch { toast.error('Failed to create document'); }
  };

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this document?')) return;
    try {
      await docsApi.delete(id);
      setDocs(p => ({ owned: p.owned.filter(d => d.doc_id !== id), shared: p.shared }));
      toast.success('Deleted');
    } catch (er) { toast.error(er.message); }
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    try {
      await docsApi.share(showShare, shareEmail);
      toast.success('Shared!');
      setShowShare(null); setShareEmail('');
    } catch (e) { toast.error(e.message); }
  };

  const allDocs = [...docs.owned.map(d => ({...d, _section: 'owned'})), ...docs.shared.map(d => ({...d, _section: 'shared'}))];
  const filtered = search ? allDocs.filter(d => d.title.toLowerCase().includes(search.toLowerCase())) : allDocs;

  return (
    <div className="min-h-screen bg-background" data-testid="docs-hub">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')}><ArrowLeft className="w-5 h-5" /></Button>
            <FileText className="w-6 h-6 text-blue-500" />
            <h1 className="text-xl font-bold text-foreground">Documents</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search docs..." className="pl-9 w-56" />
            </div>
            <Button onClick={handleCreate} data-testid="create-doc-btn"><Plus className="w-4 h-4 mr-1" /> New Document</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-36 bg-muted animate-pulse rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">{search ? 'No matching documents' : 'No documents yet'}</h2>
            <p className="text-muted-foreground mb-6">Create your first document to start collaborating.</p>
            <Button onClick={handleCreate}>Create Document</Button>
          </div>
        ) : (
          <>
            {docs.owned.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">My Documents</h2>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {docs.owned.filter(d => !search || d.title.toLowerCase().includes(search.toLowerCase())).map(doc => (
                    <Link key={doc.doc_id} to={`/docs/${doc.doc_id}`} className="group" data-testid={`doc-${doc.doc_id}`}>
                      <div className="rounded-xl border border-border bg-card hover:shadow-md transition-shadow overflow-hidden">
                        <div className="h-24 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 p-3 flex items-start">
                          <FileText className="w-8 h-8 text-blue-500" />
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">{doc.title}</h3>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(doc.updated_at).toLocaleDateString()}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={e => { e.preventDefault(); e.stopPropagation(); setShowShare(doc.doc_id); }} className="p-1 hover:bg-muted rounded"><Share2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
                              <button onClick={e => handleDelete(doc.doc_id, e)} className="p-1 hover:bg-muted rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {docs.shared.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Shared with Me</h2>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {docs.shared.filter(d => !search || d.title.toLowerCase().includes(search.toLowerCase())).map(doc => (
                    <Link key={doc.doc_id} to={`/docs/${doc.doc_id}`} className="group" data-testid={`doc-shared-${doc.doc_id}`}>
                      <div className="rounded-xl border border-border bg-card hover:shadow-md transition-shadow overflow-hidden">
                        <div className="h-24 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 p-3 flex items-start">
                          <FileText className="w-8 h-8 text-green-500" />
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-semibold text-foreground truncate">{doc.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">Shared</Badge>
                            <span className="text-[10px] text-muted-foreground">{doc.owner_name}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share Document</DialogTitle></DialogHeader>
          <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Enter user email..." data-testid="share-email-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShare(null)}>Cancel</Button>
            <Button onClick={handleShare}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
